import { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  BonusIcon,
  Button,
  ITabOption,
  Modal,
  NumberInput,
  Separator,
  Tabs,
  TextArea,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { CalculatorCash, ClientSearch, PromoCodeField } from 'components';
import { ALL_LETTERS_AND_DIGITS_REGEXP } from 'consts';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { usePayment } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EPaymentMethod } from 'types';

import { DEFAULT_VALUES } from './constants';
import s from './DepositModal.module.scss';
import { EDepositType, IDepositModalProps, IFormValues } from './interfaces';
import { PaymentPreview } from './PaymentPreview';

export const DepositModal: FC<IDepositModalProps> = ({
  className,
  onClose,
  defaultClientUUID,
}) => {
  const {
    state: {
      settings: {
        isDepositRefillEnabled,
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const isBonusCashAvailable = useIsFeatureAvailable('bonus_cash');

  const PAYMENT_METHODS: Array<ITabOption<EPaymentMethod>> = useMemo(
    () => [
      {
        iconName: 'MoneyIcon',
        value: EPaymentMethod.CASH,
        fullWidth: true,
        title: t('money.cash'),
      },
      {
        iconName: 'CreditCardIcon',
        value: EPaymentMethod.CARD,
        fullWidth: true,
        title: t('money.card'),
      },
      {
        iconName: 'CompositeIcon',
        value: EPaymentMethod.COMPOSITE,
        fullWidth: true,
        title: t('money.split'),
      },
    ],
    [t]
  );
  const [isRoleClient, setIsRoleClient] = useState<string | undefined>('');

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isSubmitting },
  } = useForm<IFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const type = watch('type');
  const clientUUID = watch('clientUUID');
  const method = watch('method');
  const sum = watch('sum');

  useEffect(() => {
    setValue('cash_sum', '', { shouldValidate: true });
    setValue('card_sum', '', { shouldValidate: true });
  }, [method, sum]);

  const { createPayment } = usePayment();

  useEffect(() => {
    if (defaultClientUUID) {
      setValue('clientUUID', defaultClientUUID);
    }
  }, [defaultClientUUID]);

  useEffect(() => {
    if (defaultClientUUID) {
      return;
    }

    reset({ ...DEFAULT_VALUES, clientUUID, type });
  }, [clientUUID, defaultClientUUID]);

  useEffect(() => {
    setValue('promoCode', null);
  }, [type, setValue]);

  return (
    <Modal className={cn(s.root, className)} onClose={() => onClose()}>
      <form
        onSubmit={handleSubmit(
          ({ type, method, sum, promoCode, clientUUID, card_sum, comment }) => {
            if (!sum) {
              return;
            }

            return createPayment({
              method:
                type === EDepositType.BONUS
                  ? EPaymentMethod.BONUS
                  : method ?? EPaymentMethod.CASH,
              clientUUID,
              promoCodeId:
                type === EDepositType.BONUS ? undefined : promoCode?.id,
              items: [
                {
                  type: type,
                  amount: 1,
                  sum,
                },
              ],
              discountValue:
                type === EDepositType.BONUS
                  ? null
                  : (sum || promoCode?.value.type === 'DISCOUNT'
                      ? promoCode?.value?.value
                      : undefined) ?? null,
              shopItems: null,
              depositSum: type === EDepositType.BONUS ? undefined : sum,
              cardSum: card_sum || 0,
              comment,
            })
              .then(() => onClose())
              .catch(console.error);
          }
        )}
        className={s.form}
      >
        <header className={s.header}>{t('deposit.modal.title')}</header>
        <div className={s.body}>
          <Controller
            control={control}
            name="clientUUID"
            render={({ field: { value, onChange } }) => (
              <div className={s.field}>
                <ClientSearch
                  chosenClientUUID={value}
                  setChosenClientUUID={onChange}
                  disabled={
                    Boolean(defaultClientUUID) ||
                    (!permissions?.has('client_browse') &&
                      !permissions?.has('client_info'))
                  }
                  openComment
                  notShowButtonDeposit
                  onFocus
                  setIsRoleClient={setIsRoleClient}
                />
              </div>
            )}
            rules={{ required: t('errors.required') }}
          />
          <div className={s['right-side']}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <div className={s.field}>
                  <Tabs
                    itemVariant="row"
                    options={[
                      {
                        iconName: 'WalletIcon',
                        value: EDepositType.DEPOSIT,
                        title: t('money.deposit'),
                      },
                      {
                        iconName: 'BonusIcon',
                        value: EDepositType.BONUS,
                        disabled:
                          !isBonusCashAvailable || !isDepositRefillEnabled,
                        title: t('bonuses'),
                      },
                    ]}
                    value={value}
                    onChange={onChange}
                    className={s.tabs}
                  />
                </div>
              )}
              control={control}
              name="type"
              rules={{ required: t('errors.required') }}
            />
            <Controller
              render={({ field, fieldState }) => (
                <div className={s.field}>
                  <NumberInput
                    {...field}
                    error={fieldState.error?.message}
                    onChange={(value) => {
                      if (value === '') return field.onChange('');

                      if (value.toString().split('.')[1]?.length > 2) {
                        return field.onChange(value.toFixed(2));
                      }

                      const formattedValue = parseFloat(value.toFixed(2));

                      field.onChange(formattedValue);
                    }}
                    title={t('payments.deposit.sum')}
                    rightContent={
                      type === EDepositType.DEPOSIT ? (
                        currency
                      ) : (
                        <BonusIcon color="primary" />
                      )
                    }
                  />
                </div>
              )}
              control={control}
              name="sum"
              rules={{
                required: t('errors.required'),
                max: {
                  value: 16000000,
                  message: t('errors.max', '16 000 000', currency),
                },
              }}
            />
            {type === EDepositType.DEPOSIT &&
              permissions?.has('promo_code_check') && (
                <FeatureHOC alias="discounts">
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <div className={s.field}>
                        <PromoCodeField
                          value={value}
                          onChange={onChange}
                          clientUUID={clientUUID}
                        />
                      </div>
                    )}
                    control={control}
                    name="promoCode"
                  />
                </FeatureHOC>
              )}
            {type === EDepositType.DEPOSIT && <PaymentPreview watch={watch} />}

            {type === EDepositType.DEPOSIT && (
              <Controller
                render={({ field: { value, onChange } }) => (
                  <div className={s.field}>
                    <Tabs
                      options={PAYMENT_METHODS}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
                control={control}
                name="method"
                rules={{ required: t('errors.required') }}
              />
            )}
            {type === EDepositType.DEPOSIT &&
              method === EPaymentMethod.COMPOSITE && (
                <div className={s.composite}>
                  <Controller
                    render={({ field, fieldState: { error, isTouched } }) => (
                      <NumberInput
                        {...field}
                        onChange={(value) => {
                          if (value === '') {
                            field.onChange('');
                            setValue('card_sum', '');
                            return;
                          }

                          if (value.toString().split('.')[1]?.length > 2) {
                            return field.onChange(value.toFixed(2));
                          }

                          const formattedValue = parseFloat(value.toFixed(2));

                          field.onChange(formattedValue);

                          if (sum === '') {
                            return setValue('card_sum', '');
                          }

                          setValue(
                            'card_sum',
                            Number((sum - formattedValue).toFixed(2))
                          );
                        }}
                        error={isTouched ? error?.message : undefined}
                        title={t('money.cash')}
                        rightContent={currency}
                        variant="primary"
                      />
                    )}
                    control={control}
                    name="cash_sum"
                    rules={{
                      validate: (value) => {
                        if (method !== EPaymentMethod.COMPOSITE) return;

                        if (value === '') return t('errors.required');

                        if (value <= 0) {
                          return t('errors.min', 0);
                        }

                        if (value >= (sum ?? 0)) {
                          return t('errors.split.minSum');
                        }
                      },
                    }}
                  />
                  <Controller
                    render={({ field, fieldState: { error, isTouched } }) => (
                      <NumberInput
                        {...field}
                        onChange={(value) => {
                          if (value === '') {
                            field.onChange('');
                            setValue('card_sum', '');
                            return;
                          }

                          if (value.toString().split('.')[1]?.length > 2) {
                            return field.onChange(value.toFixed(2));
                          }

                          const formattedValue = parseFloat(value.toFixed(2));

                          field.onChange(formattedValue);

                          if (sum === '') {
                            return setValue('cash_sum', '');
                          }

                          setValue(
                            'cash_sum',
                            Number((sum - formattedValue).toFixed(2))
                          );
                        }}
                        error={isTouched ? error?.message : undefined}
                        title={t('money.card')}
                        rightContent={currency}
                        variant="primary"
                      />
                    )}
                    control={control}
                    name="card_sum"
                    rules={{
                      required: t('errors.required'),
                      validate: (value) => {
                        if (method !== EPaymentMethod.COMPOSITE) return;

                        if (value === '') return t('errors.required');

                        if (value <= 0) {
                          return t('errors.min', 0);
                        }

                        if (value >= (sum ?? 0)) {
                          return t('errors.split.minSum');
                        }
                      },
                    }}
                  />
                </div>
              )}
            {type === EDepositType.DEPOSIT &&
              method === EPaymentMethod.CASH && (
                <CalculatorCash totalCash={sum as number} />
              )}

            {type === EDepositType.BONUS && (
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <div className={cn(s.field, s.field_max)}>
                    <TextArea
                      {...field}
                      error={error?.message}
                      title={t('deposit.fill.comment')}
                      placeholder=""
                      className={s.textarea}
                    />
                  </div>
                )}
                control={control}
                name="comment"
                rules={{
                  maxLength: {
                    value: 255,
                    message: t('errors.maxLength', 255),
                  },
                  validate: (value) => {
                    if (!value) {
                      return undefined;
                    }

                    if (!ALL_LETTERS_AND_DIGITS_REGEXP.test(value)) {
                      return t('errors.notOnlySpecial');
                    }
                  },
                }}
              />
            )}
            {type === EDepositType.BONUS && <Separator />}

            <Button
              type="submit"
              disabled={!isValid || isRoleClient === 'active_operator'}
              isLoading={isSubmitting}
              fullWidth
            >
              <div
                className={cn(
                  s.submit,
                  type === EDepositType.DEPOSIT && s.deposit
                )}
              >
                <span>
                  {t(
                    type === EDepositType.DEPOSIT
                      ? 'shoppingCart.submit'
                      : 'deposit.fill.bonuses.submit'
                  )}
                </span>
                {type === EDepositType.DEPOSIT && (
                  <span>
                    {sum ? sum.toLocaleString() : 0} {currency}
                  </span>
                )}
              </div>
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
